import { PositionType } from 'data/types';
import {
  RelativeVertex,
  setPolygonVertex,
  translatePolygonEdge
} from 'utils/space-polygon';

export type CandidatePolygon = {
  vertices: RelativeVertex[];
  positionFeet: PositionType;
};

export type CandidateValidator = (
  vertices: RelativeVertex[],
  positionFeet: PositionType
) => boolean;

export type ClampedDelta = {
  dx: number;
  dy: number;
  candidate: CandidatePolygon;
};

const SNAP_DISTANCE_FEET = 1.5;

export function findNearestNeighborVertexFeet(
  targetAbsoluteFeet: PositionType,
  neighborPolygonsAbsoluteFeet: PositionType[][],
  maxDistance: number = SNAP_DISTANCE_FEET
): PositionType | null {
  let best: PositionType | null = null;
  let bestDistance = maxDistance;
  for (const polygon of neighborPolygonsAbsoluteFeet) {
    for (const v of polygon) {
      const d = Math.hypot(
        v.x - targetAbsoluteFeet.x,
        v.y - targetAbsoluteFeet.y
      );
      if (d < bestDistance) {
        bestDistance = d;
        best = v;
      }
    }
  }
  return best;
}

function computeMinCorner(vertices: RelativeVertex[]): {
  minX: number;
  minY: number;
} {
  let minX = Infinity;
  let minY = Infinity;
  for (const v of vertices) {
    if (v.x < minX) minX = v.x;
    if (v.y < minY) minY = v.y;
  }
  return { minX, minY };
}

function normalizeCandidate(
  candidateRelative: RelativeVertex[],
  basePositionFeet: PositionType
): CandidatePolygon {
  const { minX, minY } = computeMinCorner(candidateRelative);
  return {
    positionFeet: {
      x: basePositionFeet.x + minX,
      y: basePositionFeet.y + minY
    },
    vertices: candidateRelative.map((v) => ({
      x: v.x - minX,
      y: v.y - minY
    }))
  };
}

export function buildEdgeCandidate(
  baseVerticesFeet: RelativeVertex[],
  edgeIndex: number,
  basePositionFeet: PositionType,
  dxFeet: number,
  dyFeet: number
): CandidatePolygon {
  return normalizeCandidate(
    translatePolygonEdge(baseVerticesFeet, edgeIndex, dxFeet, dyFeet),
    basePositionFeet
  );
}

export function buildVertexCandidate(
  baseVerticesFeet: RelativeVertex[],
  vertexIndex: number,
  basePositionFeet: PositionType,
  newVertexFeet: RelativeVertex
): CandidatePolygon {
  return normalizeCandidate(
    setPolygonVertex(baseVerticesFeet, vertexIndex, newVertexFeet),
    basePositionFeet
  );
}

function findMaxValidScaledDelta(
  requestedDx: number,
  requestedDy: number,
  buildCandidate: (dx: number, dy: number) => CandidatePolygon,
  isValid: CandidateValidator
): ClampedDelta | null {
  if (requestedDx === 0 && requestedDy === 0) {
    return null;
  }

  const maxMagnitude = Math.max(Math.abs(requestedDx), Math.abs(requestedDy));

  for (let mag = maxMagnitude; mag >= 1; mag -= 1) {
    const dx = Math.round((requestedDx * mag) / maxMagnitude);
    const dy = Math.round((requestedDy * mag) / maxMagnitude);
    if (dx === 0 && dy === 0) {
      return null;
    }
    const candidate = buildCandidate(dx, dy);
    if (isValid(candidate.vertices, candidate.positionFeet)) {
      return { dx, dy, candidate };
    }
  }

  return null;
}

function pickClosestDelta(
  candidates: (ClampedDelta | null)[],
  requestedDx: number,
  requestedDy: number
): ClampedDelta | null {
  let best: ClampedDelta | null = null;
  let bestDistance = Infinity;
  for (const c of candidates) {
    if (c) {
      const distance = Math.hypot(c.dx - requestedDx, c.dy - requestedDy);
      if (distance < bestDistance) {
        best = c;
        bestDistance = distance;
      }
    }
  }
  return best;
}

export function findMaxValidEdgeDelta(
  baseVerticesFeet: RelativeVertex[],
  edgeIndex: number,
  basePositionFeet: PositionType,
  requestedDx: number,
  requestedDy: number,
  isValid: CandidateValidator
): ClampedDelta | null {
  return findMaxValidScaledDelta(
    requestedDx,
    requestedDy,
    (dx, dy) =>
      buildEdgeCandidate(baseVerticesFeet, edgeIndex, basePositionFeet, dx, dy),
    isValid
  );
}

export function findMaxValidVertexDelta(
  baseVerticesFeet: RelativeVertex[],
  vertexIndex: number,
  initialVertexFeet: RelativeVertex,
  basePositionFeet: PositionType,
  requestedDx: number,
  requestedDy: number,
  isValid: CandidateValidator
): ClampedDelta | null {
  const buildCandidate = (dx: number, dy: number) =>
    buildVertexCandidate(baseVerticesFeet, vertexIndex, basePositionFeet, {
      x: initialVertexFeet.x + dx,
      y: initialVertexFeet.y + dy
    });

  const proportional = findMaxValidScaledDelta(
    requestedDx,
    requestedDy,
    buildCandidate,
    isValid
  );
  const xOnly =
    requestedDx === 0
      ? null
      : findMaxValidScaledDelta(requestedDx, 0, buildCandidate, isValid);
  const yOnly =
    requestedDy === 0
      ? null
      : findMaxValidScaledDelta(0, requestedDy, buildCandidate, isValid);

  return pickClosestDelta(
    [proportional, xOnly, yOnly],
    requestedDx,
    requestedDy
  );
}
